// Unifies the two separate workbooks for the main Leads page.
//
// The Main CRM workbook (Organizations / Opportunities -> combineLeads) and the
// Prospecting workbook (Leads tab) stay separate sources. This module only
// decides which records describe the same real prospect, so the Leads page can
// show one row per unique prospect without either sheet being written to.
//
// Identity is delegated to the same prospecting-identity module the server-side
// Leads reconciliation uses, so the "Also in Leads" badge and this merge can
// never disagree.

import {
  fallbackIdentityKeys,
  identityKeysFor,
  normalizeName,
  strongIdentityKeys,
} from '../../netlify/functions/_shared/prospecting-identity.mjs'
import type { Lead } from '../types/crm'
import type { ProspectingRecord } from '../types/prospecting'
import type { UnifiedLead } from '../types/unifiedLead'
import { cleanCell, isPlaceholder } from './transform'

/** Keeps prospecting-only records after every CRM row in the stable sort. */
const PROSPECT_ORDER_OFFSET = 1_000_000

export type CrmIdentityInput = {
  person: string
  email: string
  phone: string
  website: string
  profile: string
}

export type ProspectMatch =
  | { kind: 'none' }
  | { kind: 'ambiguous' }
  | { kind: 'matched'; leadId: string }

export type CrmIdentityIndex = {
  /** Strong identity key -> the CRM leads that claim it. */
  strong: Map<string, Set<string>>
  /** `name:<normalized>` -> the CRM leads that carry that name. */
  names: Map<string, Set<string>>
}

/**
 * Maps a CRM lead onto the record shape prospecting-identity understands.
 * Facebook and LinkedIn are joined because candidateUrls() already splits on
 * commas, so both still produce their own profile key.
 */
export function crmIdentityInput(lead: Lead): CrmIdentityInput {
  const organization = lead.organization
  const profile = [organization?.facebook, organization?.linkedin]
    .map((value) => cleanCell(value))
    .filter((value) => value && !isPlaceholder(value))
    .join(', ')

  return {
    person: cleanCell(organization?.name) || cleanCell(lead.opportunity?.name),
    email: cleanCell(organization?.email),
    phone: cleanCell(organization?.phone),
    website: cleanCell(organization?.website),
    profile,
  }
}

function nameKeysFor(lead: Lead): string[] {
  const candidates = [lead.organization?.name, lead.opportunity?.name, lead.company, lead.owner]
  const keys = new Set<string>()

  for (const candidate of candidates) {
    if (isPlaceholder(candidate)) continue
    const normalized = normalizeName(candidate)
    if (normalized) keys.add(`name:${normalized}`)
  }

  return [...keys]
}

function claim(index: Map<string, Set<string>>, key: string, leadId: string) {
  const owners = index.get(key) ?? new Set<string>()
  owners.add(leadId)
  index.set(key, owners)
}

export function buildCrmIdentityIndex(crmLeads: readonly Lead[]): CrmIdentityIndex {
  const strong = new Map<string, Set<string>>()
  const names = new Map<string, Set<string>>()

  for (const lead of crmLeads) {
    for (const key of strongIdentityKeys(crmIdentityInput(lead))) claim(strong, key, lead.id)
    for (const key of nameKeysFor(lead)) claim(names, key, lead.id)
  }

  return { strong, names }
}

/**
 * The stable identity the CRM overlay stores against (`lead_key`).
 *
 * It is the canonical reconciled identity this app already uses everywhere
 * else, never a new format:
 * - a lead that reconciled with a Prospecting row carries that record's
 *   `identityKey`, which is the smallest key of the merged identity group;
 * - a CRM-only lead uses its own lowest strong identity key - the same rule
 *   `buildIdentityGroups` applies to a group of one - and only falls back to
 *   the normalized name when the lead has no strong identifier at all.
 *
 * An empty string means the lead has no identity safe to store against - it has
 * no strong identifier, no reconciled identity, or only the mutable
 * `row:<list>:<rowNumber>` placeholder. The CRM overlay stays read-only for it
 * rather than inventing a key or following a spreadsheet row position.
 */
export function leadKeyFor(lead: Lead): string {
  const reconciled = (lead as Partial<UnifiedLead>).prospectIdentityKey
  if (reconciled && isStableLeadKey(reconciled)) return reconciled

  const own = identityKeysFor(crmIdentityInput(lead))[0] ?? ''
  return isStableLeadKey(own) ? own : ''
}

/**
 * `row:<list>:<rowNumber>` is the identity module's placeholder for a
 * prospecting row that carries no usable identifier at all. Its row number is a
 * spreadsheet position - `prospecting-records.mjs` derives it as
 * `headerIndex + index + 2` - so it changes when rows are inserted, deleted or
 * reordered. It stays a valid in-memory identity for reconciliation, but a
 * prospect that can only be identified by where it currently sits gets no CRM
 * key: the CRM overlay stays read-only rather than following the row number.
 */
function isStableLeadKey(key: string): boolean {
  return Boolean(key) && !key.startsWith('row:')
}

function resolve(index: Map<string, Set<string>>, keys: readonly string[]): ProspectMatch {
  const matched = new Set<string>()

  for (const key of keys) {
    const owners = index.get(key)
    if (!owners) continue
    for (const leadId of owners) matched.add(leadId)
  }

  if (matched.size === 0) return { kind: 'none' }
  // Conflicting evidence - the email pointing at one CRM lead and the phone at
  // another - is never resolved by picking the first. A visible duplicate is
  // safer than collapsing two real prospects into one.
  if (matched.size > 1) return { kind: 'ambiguous' }
  return { kind: 'matched', leadId: [...matched][0] }
}

/**
 * Matches one prospecting record against the CRM.
 *
 * A record that carries any usable strong identifier is matched on strong
 * identifiers only: if none of them resolve to a CRM lead the prospect stays
 * unmatched. The normalized-name fallback is reserved for records with no
 * strong identity keys at all, mirroring identityKeysFor().
 */
export function matchProspectToCrm(
  index: CrmIdentityIndex,
  record: ProspectingRecord,
): ProspectMatch {
  const strong = strongIdentityKeys(record)
  if (strong.length) return resolve(index.strong, strong)
  return resolve(index.names, fallbackIdentityKeys(record))
}

type ProspectGroup = {
  identityKey: string
  primary: ProspectingRecord
}

/** Collapses repeated rows on the Leads tab, keeping the earliest row. */
function groupProspects(records: readonly ProspectingRecord[]): ProspectGroup[] {
  const groups = new Map<string, ProspectingRecord>()

  for (const record of records) {
    const current = groups.get(record.identityKey)
    if (!current || record.rowNumber < current.rowNumber) groups.set(record.identityKey, record)
  }

  return [...groups.entries()]
    .map(([identityKey, primary]) => ({ identityKey, primary }))
    .sort((first, second) => first.primary.rowNumber - second.primary.rowNumber)
}

function prospectOnlyLead(group: ProspectGroup): UnifiedLead {
  return {
    id: `unified-prospect-${group.identityKey}`,
    order: PROSPECT_ORDER_OFFSET + group.primary.rowNumber,
    // No organization exists for this prospect yet, so none is invented.
    company: '',
    owner: group.primary.person,
    ownerIdentified: true,
    priority: ' - ',
    organization: null,
    opportunity: null,
    source: 'prospecting',
    prospect: group.primary,
    prospectIdentityKey: group.identityKey,
  }
}

/**
 * The unique unified list: CRM leads plus every Prospecting -> Leads prospect
 * that is not already one of them.
 */
export function buildUnifiedLeads(
  crmLeads: readonly Lead[],
  prospectingLeads: readonly ProspectingRecord[],
): UnifiedLead[] {
  const index = buildCrmIdentityIndex(crmLeads)
  const matchedByLeadId = new Map<string, ProspectGroup>()
  const unmatched: ProspectGroup[] = []

  for (const group of groupProspects(prospectingLeads)) {
    const match = matchProspectToCrm(index, group.primary)

    // A CRM lead is claimed once. A second prospect resolving to the same lead
    // is contradictory evidence, so it stays a separate visible record.
    if (match.kind === 'matched' && !matchedByLeadId.has(match.leadId)) {
      matchedByLeadId.set(match.leadId, group)
      continue
    }

    unmatched.push(group)
  }

  const unified: UnifiedLead[] = crmLeads.map((lead) => {
    const group = matchedByLeadId.get(lead.id)
    return {
      ...lead,
      source: group ? 'both' : 'crm',
      prospect: group?.primary ?? null,
      prospectIdentityKey: group?.identityKey ?? null,
    }
  })

  return [...unified, ...unmatched.map(prospectOnlyLead)]
}

/**
 * Prospecting identity keys that exist in the Main CRM dataset. Drives the
 * "Also in Leads" label on the Prospecting page, which means CRM membership -
 * not membership of the Prospecting workbook's own Leads tab.
 */
export function buildCrmMembership(
  crmLeads: readonly Lead[],
  prospectingRecords: readonly ProspectingRecord[],
): Set<string> {
  const index = buildCrmIdentityIndex(crmLeads)
  const membership = new Set<string>()

  for (const record of prospectingRecords) {
    if (membership.has(record.identityKey)) continue
    if (matchProspectToCrm(index, record).kind === 'matched') membership.add(record.identityKey)
  }

  return membership
}
